/*\
|*|
|*|  :: cookies.js ::
|*|
|*|  A complete cookies reader/writer framework with full unicode support.
|*|
|*|  Revision #1 - September 4, 2014
|*|
|*|  https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
|*|  https://developer.mozilla.org/User:fusionchess
|*|
|*|  This framework is released under the GNU Public License, version 3 or later.
|*|  http://www.gnu.org/licenses/gpl-3.0-standalone.html
|*|
|*|  Syntaxes:
|*|
|*|  * docCookies.setItem(name, value[, end[, path[, domain[, secure]]]])
|*|  * docCookies.getItem(name)
|*|  * docCookies.removeItem(name[, path[, domain]])
|*|  * docCookies.hasItem(name)
|*|  * docCookies.keys()
|*|
\*/

import { isClient } from '../../common/utils';

export function getItem(sKey: string) {
  if (!sKey) {
    return null;
  }
  return (
    decodeURIComponent(
      document.cookie.replace(
        new RegExp(
          '(?:(?:^|.*;)\\s*' +
            encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') +
            '\\s*\\=\\s*([^;]*).*$)|^.*$'
        ),
        '$1'
      )
    ) || null
  );
}

export function setItem(
  sKey: string,
  sValue: string,
  vEnd: Date,
  sPath: string,
  sDomain: string,
  bSecure: boolean
) {
  if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
    return false;
  }
  let sExpires = '';
  if (vEnd) {
    switch (vEnd.constructor) {
      case Number:
        sExpires =
          vEnd.getTime() === Infinity
            ? '; expires=Thu, 31-Dec-2037 23:59:59 GMT'
            : '; max-age=' + vEnd;
        break;
      case String:
        sExpires = '; expires=' + vEnd;
        break;
      case Date:
        sExpires = '; expires=' + cookieDate(vEnd);
        break;
    }
  }
  document.cookie =
    encodeURIComponent(sKey) +
    '=' +
    encodeURIComponent(sValue) +
    sExpires +
    (sDomain ? '; domain=' + sDomain : '') +
    (sPath ? '; path=' + sPath : '') +
    (bSecure ? '; secure' : '');
  return getItem(sKey);
}

export function removeItem(sKey: string, sPath: string, sDomain: string) {
  if (!hasItem(sKey)) {
    return false;
  }
  document.cookie =
    encodeURIComponent(sKey) +
    '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
    (sDomain ? '; domain=' + sDomain : '') +
    (sPath ? '; path=' + sPath : '');
  return true;
}

export function hasItem(sKey: string) {
  if (!sKey) {
    return false;
  }
  return new RegExp(
    '(?:^|;\\s*)' +
      encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') +
      '\\s*\\='
  ).test(document.cookie);
}

function keys() {
  const aKeys = document.cookie
    .replace(/((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g, '')
    .split(/\s*(?:\=[^;]*)?;\s*/);
  for (let nLen = aKeys.length, nIdx = 0; nIdx < nLen; nIdx++) {
    aKeys[nIdx] = decodeURIComponent(aKeys[nIdx] as string);
  }
  return aKeys;
}

function isEnabled(): boolean {
  const browserHasCookiesEnabled: boolean =
    isClient() && window.navigator.cookieEnabled;

  const isYleWebsite: boolean =
    isClient() && window.location.hostname.endsWith('yle.fi');

  return browserHasCookiesEnabled && isYleWebsite;
}

export default {
  getItem,
  setItem,
  removeItem,
  hasItem,
  keys,
  isEnabled,
};

function cookieDate(date: Date) {
  const padNum = function (n: number): string {
    return n < 10 ? '0' + n : n.toString();
  };

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const outString =
    '' +
    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()] +
    ', ' +
    padNum(date.getUTCDate()) +
    '-' +
    months[date.getUTCMonth()] +
    '-' +
    date.getUTCFullYear() +
    ' ' +
    padNum(date.getUTCHours()) +
    ':' +
    padNum(date.getUTCMinutes()) +
    ':' +
    padNum(date.getUTCSeconds()) +
    ' GMT';

  return outString;
}

const stringForKey = (cookie: string, key: string) => {
  const start = cookie.indexOf(key);
  const end =
    cookie.indexOf(';', start) === -1
      ? cookie.length
      : cookie.indexOf(';', start);

  return cookie.substring(start, end);
};

export const cookieValue = (key: string) => {
  const { cookie } = window.document;
  return cookie.includes(key)
    ? stringForKey(cookie, key).replace(`${key}=`, '')
    : null;
};
